import type { TableV2ColumnType } from "../types";

export type ExportCell = string | number | boolean | Date | null;

export interface ExportColumn {
  header: string;
  type?: TableV2ColumnType;
}

export interface ExportSheet {
  name: string;
  columns: ExportColumn[];
  rows: ExportCell[][];
  /** Rows to show in bold, e.g. subtotals and grand totals */
  boldRows?: Set<number>;
}

const pad = (n: number) => String(n).padStart(2, "0");

const formatDate = (date: Date, withTime: boolean) => {
  const day = `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
  return withTime
    ? `${day} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`
    : day;
};

const csvValue = (value: ExportCell, type?: TableV2ColumnType) => {
  if (value === null) return "";
  const text =
    value instanceof Date
      ? formatDate(value, type === "datetime")
      : String(value);
  // Spreadsheets run text starting with = + - @ as a formula; a leading quote keeps it as text
  const safe =
    typeof value === "string" && /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

export const buildCsv = ({ columns, rows }: ExportSheet) =>
  [
    columns.map((column) => csvValue(column.header)).join(","),
    ...rows.map((row) =>
      row.map((value, i) => csvValue(value, columns[i]?.type)).join(","),
    ),
  ].join("\r\n");

const CRC_TABLE = Array.from({ length: 256 }, (_, n) => {
  let c = n;
  for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
  return c >>> 0;
});

const encoder = new TextEncoder();

/** Bytes backed by a plain ArrayBuffer, which is what CompressionStream and Blob accept */
type Bytes = Uint8Array<ArrayBuffer>;

const crc32 = (chunks: Bytes[]) => {
  let crc = 0xffffffff;
  for (const chunk of chunks) {
    for (const byte of chunk)
      crc = CRC_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

// What CompressionStream accepts (BufferSource), named without a DOM-only global
type CompressionInput =
  CompressionStream["writable"] extends WritableStream<infer T> ? T : never;

/** Deflates with the browser's CompressionStream; returns null where it isn't available so the file is stored as is */
const deflate = async (chunks: Bytes[]) => {
  if (typeof CompressionStream === "undefined") return null;
  try {
    const input = new ReadableStream<CompressionInput>({
      start(controller) {
        chunks.forEach((chunk) => controller.enqueue(chunk));
        controller.close();
      },
    });
    const reader = input
      .pipeThrough(new CompressionStream("deflate-raw"))
      .getReader();
    const output: Bytes[] = [];
    let size = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      output.push(value);
      size += value.length;
    }
    return { chunks: output, size };
  } catch {
    return null;
  }
};

interface ZipFile {
  name: string;
  chunks: Bytes[];
}

const zip = async (files: ZipFile[]) => {
  const parts: Bytes[] = [];
  const central: Bytes[] = [];
  let offset = 0;

  for (const { name, chunks } of files) {
    const nameBytes = encoder.encode(name);
    const size = chunks.reduce((total, chunk) => total + chunk.length, 0);
    const crc = crc32(chunks);
    const compressed = await deflate(chunks);
    const body = compressed ? compressed.chunks : chunks;
    const compressedSize = compressed ? compressed.size : size;
    const method = compressed ? 8 : 0;

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);
    local.setUint16(8, method, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, compressedSize, true);
    local.setUint32(22, size, true);
    local.setUint16(26, nameBytes.length, true);

    const entry = new DataView(new ArrayBuffer(46));
    entry.setUint32(0, 0x02014b50, true);
    entry.setUint16(4, 20, true);
    entry.setUint16(6, 20, true);
    entry.setUint16(10, method, true);
    entry.setUint32(16, crc, true);
    entry.setUint32(20, compressedSize, true);
    entry.setUint32(24, size, true);
    entry.setUint16(28, nameBytes.length, true);
    entry.setUint32(42, offset, true);

    parts.push(new Uint8Array(local.buffer), nameBytes, ...body);
    central.push(new Uint8Array(entry.buffer), nameBytes);
    offset += 30 + nameBytes.length + compressedSize;
  }

  const centralSize = central.reduce((total, part) => total + part.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);

  return new Blob([...parts, ...central, new Uint8Array(end.buffer)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

const ROWS_PER_CHUNK = 2000;
const nextTask = () => new Promise((resolve) => setTimeout(resolve, 0));

const escapeXml = (text: string) =>
  text
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const columnName = (index: number) => {
  let name = "";
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    name = String.fromCharCode(65 + ((n - 1) % 26)) + name;
  }
  return name;
};

const STYLE = {
  default: 0,
  header: 1,
  currency: 2,
  percent: 3,
  date: 4,
  datetime: 5,
  bold: 6,
  boldCurrency: 7,
  boldPercent: 8,
};

const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);

const cellXml = (
  ref: string,
  value: ExportCell,
  type: TableV2ColumnType | undefined,
  bold: boolean,
) => {
  if (value === null || value === "") return "";
  if (value instanceof Date) {
    const serial = (value.getTime() - EXCEL_EPOCH_MS) / 86_400_000;
    const style = type === "datetime" ? STYLE.datetime : STYLE.date;
    return `<c r="${ref}" s="${style}"><v>${serial}</v></c>`;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    if (type === "percent") {
      const style = bold ? STYLE.boldPercent : STYLE.percent;
      return `<c r="${ref}" s="${style}"><v>${value / 100}</v></c>`;
    }
    if (type === "currency") {
      const style = bold ? STYLE.boldCurrency : STYLE.currency;
      return `<c r="${ref}" s="${style}"><v>${value}</v></c>`;
    }
    return `<c r="${ref}" s="${bold ? STYLE.bold : STYLE.default}"><v>${value}</v></c>`;
  }
  if (typeof value === "boolean") {
    return `<c r="${ref}" t="b"><v>${value ? 1 : 0}</v></c>`;
  }
  const style = bold ? ` s="${STYLE.bold}"` : "";
  return `<c r="${ref}" t="inlineStr"${style}><is><t xml:space="preserve">${escapeXml(String(value))}</t></is></c>`;
};

const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="2"><numFmt numFmtId="164" formatCode="&quot;$&quot;#,##0.00"/><numFmt numFmtId="165" formatCode="0.0%"/></numFmts>
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="3">
<fill><patternFill patternType="none"/></fill>
<fill><patternFill patternType="gray125"/></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFF2F2F2"/></patternFill></fill>
</fills>
<borders count="1"><border/></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="9">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/>
<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="14" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="22" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="164" fontId="1" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/>
<xf numFmtId="165" fontId="1" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

const columnWidth = (column: ExportColumn, rows: ExportCell[][], i: number) => {
  const sample = rows.slice(0, 200);
  const longest = Math.max(
    column.header.length,
    ...sample.map((row) => {
      const value = row[i];
      if (value instanceof Date) return 12;
      return value === null ? 0 : String(value).length;
    }),
  );
  return Math.min(Math.max(longest + 2, 8), 60);
};

/**
 * Builds the workbook a batch of rows at a time, encoding each batch straight to bytes and yielding between
 * batches, so large exports don't hold the whole sheet as one string or block the page
 */
export const buildXlsx = async ({
  name,
  columns,
  rows,
  boldRows,
}: ExportSheet) => {
  const lastColumn = columnName(Math.max(columns.length - 1, 0));
  const header = `<row r="1">${columns
    .map(
      (column, i) =>
        `<c r="${columnName(i)}1" t="inlineStr" s="${STYLE.header}"><is><t xml:space="preserve">${escapeXml(column.header)}</t></is></c>`,
    )
    .join("")}</row>`;
  const rowXml = (row: ExportCell[], r: number) => {
    const bold = !!boldRows?.has(r);
    const cells = row
      .map((value, i) =>
        cellXml(`${columnName(i)}${r + 2}`, value, columns[i]?.type, bold),
      )
      .join("");
    return `<row r="${r + 2}">${cells}</row>`;
  };
  const widths = columns
    .map(
      (column, i) =>
        `<col min="${i + 1}" max="${i + 1}" width="${columnWidth(column, rows, i)}" customWidth="1"/>`,
    )
    .join("");

  const sheet: Bytes[] = [
    encoder.encode(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
<cols>${widths}</cols>
<sheetData>${header}`),
  ];
  for (let start = 0; start < rows.length; start += ROWS_PER_CHUNK) {
    if (start > 0) await nextTask();
    sheet.push(
      encoder.encode(
        rows
          .slice(start, start + ROWS_PER_CHUNK)
          .map((row, i) => rowXml(row, start + i))
          .join(""),
      ),
    );
  }
  sheet.push(
    encoder.encode(`</sheetData>
<autoFilter ref="A1:${lastColumn}${rows.length + 1}"/>
</worksheet>`),
  );

  const sheetName = escapeXml(
    name.replace(/[\\/?*[\]:]/g, " ").slice(0, 31) || "Sheet1",
  );

  const text = (data: string) => [encoder.encode(data)];
  return zip([
    {
      name: "[Content_Types].xml",
      chunks: text(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`),
    },
    {
      name: "_rels/.rels",
      chunks: text(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`),
    },
    {
      name: "xl/workbook.xml",
      chunks: text(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="${sheetName}" sheetId="1" r:id="rId1"/></sheets>
<definedNames><definedName name="_xlnm._FilterDatabase" localSheetId="0" hidden="1">'${sheetName.replace(/'/g, "''")}'!$A$1:$${lastColumn}$${rows.length + 1}</definedName></definedNames>
</workbook>`),
    },
    {
      name: "xl/_rels/workbook.xml.rels",
      chunks: text(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`),
    },
    { name: "xl/styles.xml", chunks: text(STYLES_XML) },
    { name: "xl/worksheets/sheet1.xml", chunks: sheet },
  ]);
};
